import { toDate, intlDisplayOptions, formatWithDisplayFormat } from './date';
import { TIMEZONE_LABELS, fullTimezoneToShortTimezone, countryToTimezones, shortTimezoneToFullTimezone } from './timezone.data';
export { shortTimezoneToFullTimezone } from './timezone.data';
export { TIMEZONE_LABELS, fullTimezoneToShortTimezone, countryToTimezones, TIMEZONE_REGIONS, TIMEZONE_REGION_GROUPS, TIMEZONE_REGION_ORDER } from './timezone.data';
/**
 * Resolves a timezone identifier to its human-readable display label — the
 * same text `HBTimezoneInput` shows (e.g. `"Pacific Time - US & Canada"`).
 * Accepts either a short server code (`TShortTimezone`, e.g. `'PDT'`) or a
 * full IANA id (`TFullTimezone`, e.g. `'America/Los_Angeles'`); also tolerates
 * a loose `string` (server data isn't always well-typed) and `null`/`undefined`.
 *
 * Resolution order: an IANA id with a known label wins; otherwise a short
 * code is mapped to its IANA id via `shortTimezoneToFullTimezone` and that
 * id's label is used. DST siblings resolve to the same label (`'PDT'` and
 * `'PST'` both → "Pacific Time - US & Canada") because the label describes
 * the region, not the offset.
 *
 * Falls back to returning the input unchanged when nothing resolves (all 85
 * short codes currently map to an entry in `TIMEZONE_LABELS`, so this is
 * defensive), and to `''` for `null`/`undefined` — never `undefined` or the
 * string `"undefined"`.
 *
 * @remarks Some `TIMEZONE_LABELS` entries have no friendly name yet and use
 * the IANA id itself (e.g. `'EET'` → `'Africa/Cairo'`). This matches what
 * `HBTimezoneInput` shows for those zones.
 *
 * Known data gotcha: the short code `'BST'` maps to `Asia/Dhaka`
 * (Bangladesh), not British Summer Time — the UK is `'GMT'` → `Europe/London`.
 *
 * @example
 * getTimezoneLabel('PDT') // → 'Pacific Time - US & Canada'
 * getTimezoneLabel('PST') // → 'Pacific Time - US & Canada' (DST sibling)
 * getTimezoneLabel('America/Los_Angeles') // → 'Pacific Time - US & Canada'
 * getTimezoneLabel('unknown') // → 'unknown' (returned unchanged)
 * getTimezoneLabel(undefined) // → ''
 */
export function getTimezoneLabel(timezone) {
    if (!timezone) {
        return '';
    }
    const labels = TIMEZONE_LABELS;
    // Own-property checks, so inputs like 'constructor' or '__proto__' don't
    // resolve through Object.prototype and return a non-string.
    if (hasOwn(labels, timezone)) {
        return labels[timezone];
    }
    const shortToFull = shortTimezoneToFullTimezone;
    if (hasOwn(shortToFull, timezone) &&
        hasOwn(labels, shortToFull[timezone])) {
        return labels[shortToFull[timezone]];
    }
    return timezone;
}
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
/** IANA timezone string used as fallback when no explicit timezone is provided. */
export const DEFAULT_TIMEZONE = 'America/Los_Angeles';
/**
 * Returns the browser's local IANA timezone string via the `Intl` API, or
 * `undefined` when the browser does not expose one or resolves an invalid zone.
 * Use this variant when the caller has its own fallback chain (e.g. a company
 * timezone) that should take priority over `DEFAULT_TIMEZONE`.
 */
export function getValidBrowserTimezone() {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!resolved) {
        return undefined;
    }
    try {
        // Probe with the same op dayjs/Intl trip on. Browsers that can't resolve
        // a real IANA zone return "Etc/Unknown", which is truthy but invalid.
        new Intl.DateTimeFormat(undefined, { timeZone: resolved });
        return resolved;
    }
    catch {
        return undefined;
    }
}
/**
 * Returns the browser's local IANA timezone string via the `Intl` API.
 * Falls back to `DEFAULT_TIMEZONE` when the browser does not expose timezone info.
 */
export function getBrowserTimezone() {
    return getValidBrowserTimezone() ?? DEFAULT_TIMEZONE;
}
/**
 * Formats a UTC datetime value by converting to the target timezone first, then
 * formatting per locale. Use for UTC timestamp fields (meeting.start_time, payment.processed_at).
 *
 * @example
 * formatDateByTimezone(Date.UTC(2026, 4, 17, 23, 0, 0), 'en-GB', 'Europe/London')
 * // → "18/05/2026, 00:00" (BST = UTC+1)
 * formatDateByTimezone(Date.UTC(2026, 5, 9, 18, 30, 0), 'en-US', 'America/Los_Angeles', 'long')
 * // → "Tue, Jun 9, 2026, 11:30 AM"
 */
export function formatDateByTimezone(value, locale, timezone, format = 'datetime') {
    const formatter = new Intl.DateTimeFormat(locale, intlDisplayOptions(format, timezone));
    return formatWithDisplayFormat(formatter, toDate(value), format);
}
/**
 * Whether this browser accepts `timeZoneName: 'shortOffset'` (Chrome/Edge 95+,
 * Safari 15.4+, Firefox 91+). Older browsers throw a RangeError from the
 * constructor. Probed once with `UTC`, which every browser accepts, so a throw
 * here can only mean the option is unsupported. `getTimezones` calls
 * `getOffsetMinutes` for ~118 zones, so this must not be re-probed per call.
 */
const SUPPORTS_SHORT_OFFSET = (() => {
    try {
        new Intl.DateTimeFormat('en-US', {
            timeZone: 'UTC',
            timeZoneName: 'shortOffset'
        });
        return true;
    }
    catch {
        return false;
    }
})();
function getOffsetMinutes(id, now) {
    return SUPPORTS_SHORT_OFFSET
        ? getOffsetMinutesFromShortOffset(id, now)
        : getOffsetMinutesFromWallClock(id, now);
}
function getOffsetMinutesFromShortOffset(id, now) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: id,
        timeZoneName: 'shortOffset'
    }).formatToParts(now);
    const tzName = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT+0';
    const match = tzName.match(/GMT([+-])(\d+)(?::(\d+))?/);
    if (!match) {
        return 0;
    }
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3] ?? '0', 10);
    return sign * (hours * 60 + minutes);
}
/**
 * Offset for browsers without `shortOffset`: read the wall-clock time `id`
 * shows at `now`, treat it as UTC, and subtract `now`. Only needs
 * `formatToParts` (Chrome 57, Safari 11) and `hourCycle` (Chrome 73, Safari 13).
 * An invalid `id` still throws a RangeError, as the `shortOffset` path does.
 */
function getOffsetMinutesFromWallClock(id, now) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: id,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
    }).formatToParts(now);
    const part = (type) => Number(parts.find(p => p.type === type)?.value);
    const wallClockAsUtc = Date.UTC(part('year'), part('month') - 1, part('day'), 
    // Some engines report midnight as 24 (Chrome 80 does with hour12: false).
    part('hour') % 24, part('minute'), part('second'));
    // The formatted parts carry no milliseconds, so drop them from `now` too.
    const instant = Math.floor(now.getTime() / 1000) * 1000;
    return Math.round((wallClockAsUtc - instant) / 60_000);
}
/** Returns "GMT-8" style offset string (DST-aware). Includes minutes for half/quarter-hour zones (e.g. "GMT+5:30"). */
export function formatShortUtcOffset(id, now = new Date()) {
    const offset = getOffsetMinutes(id, now);
    const sign = offset >= 0 ? '+' : '-';
    const abs = Math.abs(offset);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m
        ? `GMT${sign}${h}:${String(m).padStart(2, '0')}`
        : `GMT${sign}${h}`;
}
/** Returns "GMT -08:00" style offset string (DST-aware). */
export function formatLongUtcOffset(id, now = new Date()) {
    const offset = getOffsetMinutes(id, now);
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hh = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const mm = String(absOffset % 60).padStart(2, '0');
    return `GMT ${sign}${hh}:${mm}`;
}
/**
 * Whether `value` is an IANA zone identifier (`'America/Toronto'`) that this
 * browser can use.
 *
 * A region prefix is required, and that is not cosmetic: the tz database also
 * defines bare `EST`, `MST` and `HST` as fixed-offset zones with no DST, so
 * `Intl` accepts `'EST'` and reads it as UTC-5 all year rather than as New
 * York. Those strings are HoneyBook short codes, not zones. Matches the
 * server's `TimeZoneService.canonical_iana`.
 */
export function isIanaTimezone(value) {
    if (!value || !value.includes('/')) {
        return false;
    }
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * The zone to use for a record that carries an IANA field next to a legacy
 * short code, e.g. a company's `default_timezone_iana` and `default_timezone`.
 *
 * The IANA field wins whenever it holds a real zone: the server resolves it
 * with the company's country, so a Toronto company is `America/Toronto` there,
 * while `shortTimezoneToFullTimezone['EST']` can only ever say New York. The
 * short-code lookup is the fallback for responses that don't carry the IANA
 * field yet.
 *
 * For display and date math only. Never derive an IANA value from the short
 * code and SEND it: the server prefers a sent IANA zone over its own
 * country-aware resolution, so a country-blind guess would overwrite a correct
 * answer (a UK company's `BST` is `Asia/Dhaka` in the short-code map).
 */
export function resolveTimezoneId(iana, shortCode) {
    if (isIanaTimezone(iana)) {
        return iana;
    }
    const shortToFull = shortTimezoneToFullTimezone;
    return shortCode && hasOwn(shortToFull, shortCode)
        ? shortToFull[shortCode]
        : undefined;
}
/**
 * Returns sorted TimezoneInfo[] for all zones that have a short-code mapping.
 * Sorted by offsetMinutes ascending, then by label for ties.
 *
 * `include` adds zones the list does not carry. Pass the value a picker is
 * showing, so a stored zone outside the list (e.g. `America/Toronto`) is still
 * displayed and selected, rather than rendering blank and inviting a save of
 * some other zone. An included zone without a friendly label is labelled by
 * its IANA id, like the unnamed `TIMEZONE_LABELS` entries. Values that are not
 * IANA zones are ignored.
 * @remarks Builds one Intl.DateTimeFormat per zone (~118). Result is deterministic for a
 * given `now` — callers rendering a picker should memoize (e.g. useMemo) rather than call per render.
 */
export function getTimezones(now = new Date(), include = []) {
    const result = [];
    const labels = TIMEZONE_LABELS;
    for (const extra of new Set(include)) {
        const listed = isIanaTimezone(extra) &&
            hasOwn(labels, extra) &&
            extra in fullTimezoneToShortTimezone;
        if (!isIanaTimezone(extra) || listed) {
            continue;
        }
        // Outside the TFullTimezone union by construction; still a real zone,
        // which is all a picker and Intl need.
        const id = extra;
        result.push({
            id,
            label: hasOwn(labels, extra) ? labels[extra] : extra,
            region: extra.split('/')[0],
            offsetMinutes: getOffsetMinutes(id, now)
        });
    }
    for (const [id, label] of Object.entries(TIMEZONE_LABELS)) {
        if (!(id in fullTimezoneToShortTimezone)) {
            continue;
        }
        const region = id.includes('/') ? id.split('/')[0] : id;
        const offsetMinutes = getOffsetMinutes(id, now);
        result.push({ id, label, region, offsetMinutes });
    }
    return result.sort((a, b) => a.offsetMinutes !== b.offsetMinutes
        ? a.offsetMinutes - b.offsetMinutes
        : a.label.localeCompare(b.label));
}
/**
 * Returns the suggested IANA timezone IDs for a given country.
 * US and CA are merged. Returns [] for countries not in the map.
 */
export function getSuggestedTimezoneIds(country) {
    if (country === 'US' || country === 'CA') {
        return [
            ...new Set([
                ...(countryToTimezones['US'] ?? []),
                ...(countryToTimezones['CA'] ?? [])
            ])
        ];
    }
    return countryToTimezones[country] ?? [];
}
//# sourceMappingURL=timezone.js.map